import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { testimonials, stats } from '@/mocks/testimonials';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9sm12h4ne17ei0sdjpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-100 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li className="text-foreground-300">/</li>
              <li className="text-foreground-900 font-medium">关于我们</li>
            </ol>
          </div>
        </nav>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 mb-6">
              关于蘑菇社区
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
              蘑菇社区诞生于一群热爱生活、热衷分享的年轻人的共同愿景。我们相信，每个人都有独特的故事和值得被听见的声音。在这里，兴趣是桥梁，分享是语言，每一次互动都是一次温暖的相遇。
            </p>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              自2024年创立以来，蘑菇社区已经汇聚了超过<strong className="text-foreground-900">18万</strong>活跃用户，
              积累了<strong className="text-foreground-900">1.2万+</strong>篇优质原创文章，
              覆盖旅行、美食、科技、读书、健身、艺术等<strong className="text-foreground-900">六大核心兴趣领域</strong>。
              我们始终秉持"用心连接每一个有趣的灵魂"的理念，致力于打造中文互联网上最温暖、最有价值的兴趣交流社区。
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-background-50 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-accent-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                    {stat.value}
                  </div>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                听听蘑菇社区的用户怎么说
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-100 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-primary-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                有任何问题或合作意向，欢迎随时联系蘑菇社区团队
              </p>
            </div>

            <form
              id="about-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-xl p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入你的留言内容（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <div className="honeypot-field">
                <label htmlFor="contact-hp">Website</label>
                <input
                  type="text"
                  id="contact-hp"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formStatus === 'success' && (
                <div className="p-4 bg-accent-100 text-accent-800 rounded-lg text-sm">
                  感谢你的留言！蘑菇社区团队会在1-2个工作日内与你联系。
                </div>
              )}
              {formStatus === 'error' && formError && (
                <div className="p-4 bg-red-100 text-red-700 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                发送留言
              </button>
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">邮箱</h3>
                <p className="text-xs text-foreground-500">hello@mushroomcommunity.com</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-wechat-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-500">蘑菇社区官方</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">地址</h3>
                <p className="text-xs text-foreground-500">北京市朝阳区</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}